import React, { useState, useEffect, useRef } from "react";
import { Bar, Pie, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import SubPageLayout from "../../components/shared/SubPageLayout";
import { useDropzone } from "react-dropzone";
import api from "../../utils/apiConfig";
import { getHoldings, getSecurityTypes } from "../../utils/apiConfig";
import { FaSyncAlt } from "react-icons/fa";
import { useAuth } from "../../contexts/AuthContext";
import Modal from "../../components/shared/Modal";
import HouseholdView from "./allocations/HouseholdView";

import logger from '../../utils/logger';
import { useTheme } from '../../contexts/ThemeContext';
import { useDemo } from "../../contexts/DemoContext";
import { useNavigate, useParams } from "react-router-dom";
import SidebarTemplate from "../../components/shared/SidebarTemplate";
import { MODAL_TYPES, MODAL_TEMPLATES } from "../../demo/annualReviewDemoLogic";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

// Mock data for advisors, households, and accounts
const mockAdvisors = [
  { id: 1, name: "John Smith", aum: 5200000 },
  { id: 2, name: "Sarah Johnson", aum: 4800000 },
  { id: 3, name: "Michael Brown", aum: 4200000 },
];

const mockHouseholds = [
  { id: 1, advisorId: 1, name: "Smith Family", aum: 2500000 },
  { id: 2, advisorId: 1, name: "Johnson Family", aum: 1800000 },
  { id: 3, advisorId: 2, name: "Williams Family", aum: 2200000 },
];

const mockAccounts = [
  {
    id: 1,
    householdId: 1,
    type: "Investment",
    number: "INV001",
    balance: 1500000,
  },
  {
    id: 2,
    householdId: 1,
    type: "Retirement",
    number: "RET001",
    balance: 1000000,
  },
  {
    id: 3,
    householdId: 2,
    type: "Investment",
    number: "INV002",
    balance: 1800000,
  },
];

// Mock data for holdings
const mockHoldings = [
  {
    symbol: "AAPL",
    name: "Apple Inc.",
    shares: 1500,
    price: 175.43,
    marketValue: 263145,
    weight: 15.2,
  },
  {
    symbol: "MSFT",
    name: "Microsoft Corp.",
    shares: 800,
    price: 338.11,
    marketValue: 270488,
    weight: 14.8,
  },
  {
    symbol: "GOOGL",
    name: "Alphabet Inc.",
    shares: 250,
    price: 141.18,
    marketValue: 35295,
    weight: 12.5,
  },
  {
    symbol: "AMZN",
    name: "Amazon.com Inc.",
    shares: 400,
    price: 178.22,
    marketValue: 71288,
    weight: 11.3,
  },
  {
    symbol: "NVDA",
    name: "NVIDIA Corp.",
    shares: 150,
    price: 881.86,
    marketValue: 132279,
    weight: 10.8,
  },
  {
    symbol: "META",
    name: "Meta Platforms Inc.",
    shares: 300,
    price: 509.58,
    marketValue: 152874,
    weight: 8.7,
  },
  {
    symbol: "BRK.B",
    name: "Berkshire Hathaway Inc.",
    shares: 200,
    price: 411.56,
    marketValue: 82312,
    weight: 7.2,
  },
  {
    symbol: "JPM",
    name: "JPMorgan Chase & Co.",
    shares: 250,
    price: 183.27,
    marketValue: 45817.5,
    weight: 6.8,
  },
  {
    symbol: "V",
    name: "Visa Inc.",
    shares: 175,
    price: 279.87,
    marketValue: 48977.25,
    weight: 6.5,
  },
  {
    symbol: "JNJ",
    name: "Johnson & Johnson",
    shares: 150,
    price: 157.89,
    marketValue: 23683.5,
    weight: 6.2,
  },
];

// Global Search Component
const GlobalSearch = ({ onSearch }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const handleSearch = (value) => {
    setSearchTerm(value);
    // Mock search results - in real app, this would query your backend
    const results = [
      ...mockAdvisors
        .filter((advisor) =>
          advisor.name.toLowerCase().includes(value.toLowerCase()),
        )
        .map((advisor) => ({ ...advisor, type: "advisor" })),
      ...mockHouseholds
        .filter((household) =>
          household.name.toLowerCase().includes(value.toLowerCase()),
        )
        .map((household) => ({ ...household, type: "household" })),
      ...mockAccounts
        .filter((account) =>
          account.number.toLowerCase().includes(value.toLowerCase()),
        )
        .map((account) => ({ ...account, type: "account" })),
    ];
    setSearchResults(results);
    setShowResults(value.length > 0);
  };

  return (
    <div className="relative mb-6">
      <div className="relative">
        <input
          type="text"
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Search advisors, households, or accounts..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg
            className="h-5 w-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {showResults && (
        <div className="absolute w-full mt-1 bg-surface-default dark:bg-surface-default border border-gray-300 rounded-lg shadow-lg dark:shadow-none z-50">
          {searchResults.length > 0 ? (
            searchResults.map((result, index) => (
              <div
                key={`${result.type}-${result.id}`}
                className="p-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer border-b last:border-b-0"
                onClick={() => {
                  if (result.type === "advisor") {
                    onSearch({ type: "advisor", data: result });
                  } else if (result.type === "household") {
                    onSearch({ type: "household", data: result });
                  } else if (result.type === "account") {
                    onSearch({ type: "account", data: result });
                  }
                  setShowResults(false);
                  setSearchTerm("");
                }}
              >
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {result.type === "advisor"
                      ? result.name
                      : result.type === "household"
                        ? result.name
                        : `Account ${result.number}`}
                  </span>
                  <span className="ml-2 px-2 py-1 text-xs font-medium text-gray-500 dark:text-gray-500 bg-gray-100 rounded-full">
                    {result.type.charAt(0).toUpperCase() + result.type.slice(1)}
                  </span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-500">
                  {result.type === "advisor"
                    ? `AUM: $${(result.aum / 1000000).toFixed(1)}M`
                    : result.type === "household"
                      ? `AUM: $${(result.aum / 1000000).toFixed(1)}M`
                      : `Balance: $${(result.balance / 1000000).toFixed(1)}M`}
                </div>
              </div>
            ))
          ) : (
            <div className="p-3 text-sm text-gray-500 dark:text-gray-500">No results found</div>
          )}
        </div>
      )}
    </div>
  );
};

// Add CSV Preview Component
const CSVPreview = ({ records, onConfirm, onCancel }) => {
  const [validationIssues, setValidationIssues] = useState({});

  useEffect(() => {
    // Validate records
    const issues = {};
    records.forEach((record, index) => {
      const recordIssues = [];

      // Check for primary account holder
      if (!record[4]) {
        recordIssues.push("Missing primary account holder");
      } else if (!record[4].includes(",")) {
        // Check if this is a trust account by looking at account_name_1
        const isTrustAccount =
          record[1]?.includes("TTEE") ||
          record[1]?.includes("TRUST") ||
          record[2]?.includes("TRUST") ||
          record[5] === "Liv Trust";

        // Only require comma format for non-trust accounts
        if (!isTrustAccount) {
          recordIssues.push(
            'Invalid primary account holder format (should be "LASTNAME, FIRSTNAME")',
          );
        }
      }

      // Check for secondary account holder in joint accounts
      if (record[5] === "Jt Ten" || record[5] === "Dsg Ben Jt") {
        if (!record[2]) {
          recordIssues.push(
            "Joint account missing secondary account holder in Account name 2",
          );
        }
      }

      // Check for registration type
      if (!record[5]) {
        recordIssues.push("Missing registration type");
      }

      // Check for account number
      if (!record[0]) {
        recordIssues.push("Missing account number");
      }

      if (recordIssues.length > 0) {
        issues[index] = recordIssues;
      }
    });
    setValidationIssues(issues);
  }, [records]);

  return (
    <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Preview Records
      </h2>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="text-left text-sm text-gray-600 dark:text-gray-400">
              <th className="py-2 px-4">Account</th>
              <th className="py-2 px-4">Account Name 1</th>
              <th className="py-2 px-4">Account Name 2</th>
              <th className="py-2 px-4">Primary Account Holder</th>
              <th className="py-2 px-4">Registration Type</th>
              <th className="py-2 px-4">Total Value</th>
              <th className="py-2 px-4">Issues</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record, index) => (
              <tr
                key={index}
                className={`border-t border-gray-100 dark:border-gray-800 ${
                  validationIssues[index] ? "bg-red-50" : ""
                }`}
              >
                <td className="py-2 px-4">{record[0]}</td>
                <td className="py-2 px-4">{record[1]}</td>
                <td className="py-2 px-4">{record[2]}</td>
                <td className="py-2 px-4">{record[4]}</td>
                <td className="py-2 px-4">{record[5]}</td>
                <td className="py-2 px-4">{record[7]}</td>
                <td className="py-2 px-4">
                  {validationIssues[index] && (
                    <div className="text-red-600 text-sm">
                      {validationIssues[index].map((issue, i) => (
                        <div key={i}>{issue}</div>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-6 flex justify-end space-x-4">
        <button
          className="px-4 py-2 bg-gray-200 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          className={`px-4 py-2 rounded ${
            Object.keys(validationIssues).length > 0
              ? "bg-gray-300 text-gray-500 dark:text-gray-500 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
          onClick={onConfirm}
          disabled={Object.keys(validationIssues).length > 0}
        >
          Confirm Upload
        </button>
      </div>
    </div>
  );
};

// Update CSVUpload Component
const CSVUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [previewRecords, setPreviewRecords] = useState(null);

  const processCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split("\n");
      const records = [];
      let isHeaderFound = false;

      lines.forEach((line) => {
        if (line.trim() === "") return;

        // Parse CSV, handling quoted values
        const values = line.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g);
        if (!values) return;

        // Clean up quoted values
        const cleanValues = values.map((val) =>
          val.replace(/^"|"$/g, "").trim(),
        );

        if (cleanValues[0] === "Account") {
          isHeaderFound = true;
          return;
        }

        if (
          isHeaderFound &&
          !cleanValues[0].includes("Records exported") &&
          !cleanValues[0].includes("Total value")
        ) {
          records.push(cleanValues);
        }
      });

      setPreviewRecords(records);
    };
    reader.readAsText(file);
  };

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file type
    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      setUploadStatus({ type: "error", message: "Please upload a CSV file" });
      return;
    }

    setUploading(false);
    setUploadStatus(null);
    processCSV(file);
  };

  const handleConfirmUpload = async () => {
    if (!previewRecords) return;

    setUploading(true);
    setUploadStatus(null);

    try {
      // Create CSV content with headers
      const headers = [
        "Account",
        "Account Name 1",
        "Account Name 2",
        "Account Name 3",
        "Primary Account Holder",
        "Registration Type",
        "Account Description",
        "Total Value",
      ];
      const csvContent = [
        headers.join(","),
        ...previewRecords.map((record) => record.join(",")),
      ].join("\n");

      // Create FormData and append the file
      const formData = new FormData();
      const blob = new Blob([csvContent], { type: "text/csv" });
      formData.append("file", blob, "managed_client_assets.csv");

      // Make the API request
      const response = await api.post(
        "/advisor-portal/managed-client-assets/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setUploadStatus({
        type: "success",
        message: `Successfully processed ${response.data.recordsProcessed} accounts`,
      });
    } catch (error) {
      logger.error("Upload error:", error);
      setUploadStatus({
        type: "error",
        message:
          error.response?.data?.error ||
          "Failed to upload file. Please try again.",
      });
    } finally {
      setUploading(false);
      setPreviewRecords(null);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    maxFiles: 1,
  });

  if (previewRecords) {
    return (
      <CSVPreview
        records={previewRecords}
        onConfirm={handleConfirmUpload}
        onCancel={() => setPreviewRecords(null)}
      />
    );
  }

  return (
    <div className="mb-6">
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Update Account Values
        </h2>
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${
            isDragActive
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 hover:border-blue-500"
          }`}
        >
          <input {...getInputProps()} />
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {isDragActive
                ? "Drop the CSV file here"
                : "Drag and drop a CSV file here, or click to select"}
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
              CSV file should contain account values and balances
            </p>
          </div>
        </div>
        {uploading && (
          <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
            Uploading and processing file...
          </div>
        )}
        {uploadStatus && (
          <div
            className={`mt-4 text-center text-sm ${
              uploadStatus.type === "success"
                ? "text-green-600"
                : "text-red-600"
            }`}
          >
            {uploadStatus.message}
          </div>
        )}
      </div>
    </div>
  );
};

// Skeleton loader for table rows
const TableSkeleton = ({ rows = 10 }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full">
      <thead>
        <tr className="text-left text-sm text-gray-600 dark:text-gray-400">
          <th className="py-3 px-4">Symbol</th>
          <th className="py-3 px-4">Security Type</th>
          <th className="py-3 px-4 text-right">Total Shares</th>
          <th className="py-3 px-4 text-right">Total Market Value</th>
          <th className="py-3 px-4 text-right"># Accounts</th>
          <th className="py-3 px-4 text-right">Latest As of Date</th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, idx) => (
          <tr key={idx} className="border-t border-gray-200 dark:border-gray-700 animate-pulse">
            {Array.from({ length: 6 }).map((_, cidx) => (
              <td key={cidx} className="py-3 px-4">
                <div className="h-4 bg-gray-200 rounded w-full" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TIME_RANGES = [
  { label: "Since Inception", value: "all" },
  { label: "3Y", value: "3y" },
  { label: "1Y", value: "1y" },
  { label: "6M", value: "6m" },
  { label: "3M", value: "3m" },
  { label: "1M", value: "1m" },
];

function getDateCutoff(range, allDates) {
  if (range === "all") return null;
  const now = new Date();
  let cutoff;
  switch (range) {
    case "3y":
      cutoff = new Date(now);
      cutoff.setFullYear(now.getFullYear() - 3);
      break;
    case "1y":
      cutoff = new Date(now);
      cutoff.setFullYear(now.getFullYear() - 1);
      break;
    case "6m":
      cutoff = new Date(now);
      cutoff.setMonth(now.getMonth() - 6);
      break;
    case "3m":
      cutoff = new Date(now);
      cutoff.setMonth(now.getMonth() - 3);
      break;
    case "1m":
      cutoff = new Date(now);
      cutoff.setMonth(now.getMonth() - 1);
      break;
    default:
      return null;
  }
  // Find the first date >= cutoff
  for (let i = 0; i < allDates.length; ++i) {
    if (new Date(allDates[i]) >= cutoff) return allDates[i];
  }
  return allDates[0];
}

const PortfolioPerformanceChart = ({ holdings }) => {
  const [perfData, setPerfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState("all");
  const sharesBySymbol = useRef({});
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [usingCustom, setUsingCustom] = useState(false);

  // Build sharesBySymbol from holdings (use most recent quantity for each symbol)
  useEffect(() => {
    const map = {};
    holdings.forEach((h) => {
      const symbol = h.symbol?.toUpperCase();
      if (!symbol) return;
      if (
        !map[symbol] ||
        new Date(h.as_of_date) > new Date(map[symbol].as_of_date)
      ) {
        map[symbol] = {
          quantity: parseFloat(h.quantity) || 0,
          as_of_date: h.as_of_date,
        };
      }
    });
    sharesBySymbol.current = Object.fromEntries(
      Object.entries(map).map(([k, v]) => [k, v.quantity]),
    );
  }, [holdings]);

  useEffect(() => {
    setLoading(true);
    fetch("/data/all_holdings_weekly_closes.json")
      .then((r) => r.json())
      .then((json) => {
        // Use the first symbol as the reference for weekly dates
        const refSymbol = Object.keys(json)[0];
        // Only use dates from 2022-04-01 onward
        const refDates = json[refSymbol]
          .map(([date]) => date)
          .filter((date) => date >= "2022-04-01");
        // For each date, sum value for all holdings
        const values = refDates.map((date) => {
          let total = 0;
          for (const symbol in json) {
            const arr = json[symbol];
            const entry = arr.find(([d]) => d === date);
            const price = entry ? entry[1] : null;
            const shares = sharesBySymbol.current[symbol] || 0;
            if (price != null) total += price * shares;
          }
          return { date, value: total };
        });
        setPerfData({ allDates: refDates, values });
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [holdings]);

  if (loading)
    return (
      <div className="h-[300px] flex items-center justify-center">
        Loading performance...
      </div>
    );
  if (error)
    return <div className="h-[300px] text-red-600">Error: {error}</div>;
  if (!perfData) return null;

  // Filter by range or custom
  let { allDates, values } = perfData;
  let filteredDates = allDates;
  let filteredValues = values;
  let startIdx = 0,
    endIdx = allDates.length - 1;

  if (usingCustom && customStart && customEnd) {
    startIdx = allDates.findIndex((d) => d >= customStart);
    endIdx = allDates.findIndex((d) => d > customEnd);
    if (startIdx === -1 || (endIdx !== -1 && endIdx <= startIdx)) {
      filteredDates = [];
      filteredValues = [];
    } else {
      filteredDates = allDates.slice(
        startIdx,
        endIdx === -1 ? undefined : endIdx,
      );
      filteredValues = values.slice(
        startIdx,
        endIdx === -1 ? undefined : endIdx,
      );
    }
  } else {
    let cutoff = getDateCutoff(range, allDates);
    if (cutoff) {
      const idx = allDates.indexOf(cutoff);
      filteredDates = allDates.slice(idx);
      filteredValues = values.slice(idx);
      startIdx = idx;
      endIdx = allDates.length - 1;
    }
  }

  const chartData = {
    labels: filteredDates,
    datasets: [
      {
        label: "Portfolio Value",
        data: filteredValues.map((v) => v.value),
        borderColor: "#1E3A8A",
        backgroundColor: "rgba(30,58,138,0.1)",
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 6,
      },
    ],
  };
  const chartOptions = {
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const idx = ctx.dataIndex;
            const startVal = filteredValues[0]?.value;
            const currVal = filteredValues[idx]?.value;
            const pct =
              startVal !== 0 ? ((currVal - startVal) / startVal) * 100 : null;
            return [
              `Value: $${currVal?.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
              pct !== null
                ? `Return: ${pct >= 0 ? "+" : ""}${pct?.toFixed(2)}%`
                : "",
            ];
          },
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (v) =>
            `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
        },
      },
    },
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Portfolio Performance
        </h2>
        <div className="flex space-x-2">
          {TIME_RANGES.map((opt) => (
            <button
              key={opt.value}
              className={`px-2 py-1 rounded text-xs ${range === opt.value ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 dark:text-gray-300 hover:bg-gray-300"}`}
              onClick={() => setRange(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div className="h-[300px]">
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

const SymbolDetailModal = ({ symbol, open, onClose, performanceData }) => {
  if (!open) return null;
  // Dummy data for symbol details
  const dummyDetails = {
    sector: "Technology",
    assetClass: "Equity",
    name: symbol,
    nextEarnings: "2024-08-15",
    financials: {
      revenue: "$394B",
      netIncome: "$99B",
      peRatio: "28.5",
    },
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none p-6 w-full max-w-2xl relative">
        <button
          className="absolute top-2 right-2 text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
          onClick={onClose}
        >
          &times;
        </button>
        <h2 className="text-xl font-semibold mb-2">
          {dummyDetails.name} ({symbol})
        </h2>
        <div className="mb-4">
          <span className="text-gray-600 dark:text-gray-400">Sector:</span> {dummyDetails.sector} |{" "}
          <span className="text-gray-600 dark:text-gray-400">Asset Class:</span>{" "}
          {dummyDetails.assetClass}
        </div>
        <div className="mb-4">
          <span className="text-gray-600 dark:text-gray-400">Next Earnings:</span>{" "}
          {dummyDetails.nextEarnings}
        </div>
        <div className="mb-4">
          <span className="text-gray-600 dark:text-gray-400">Financials:</span>
          <ul className="ml-4">
            <li>Revenue: {dummyDetails.financials.revenue}</li>
            <li>Net Income: {dummyDetails.financials.netIncome}</li>
            <li>P/E Ratio: {dummyDetails.financials.peRatio}</li>
          </ul>
        </div>
        <div className="mb-4">
          <h3 className="font-semibold mb-2">Historical Performance</h3>
          {/* Use a Line chart for performanceData if available */}
          {performanceData ? (
            <Line
              data={performanceData}
              options={{
                responsive: true,
                plugins: { legend: { display: false } },
              }}
            />
          ) : (
            <div className="text-gray-500 dark:text-gray-500">No performance data available.</div>
          )}
        </div>
      </div>
    </div>
  );
};

const AdvisorView = () => {
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { user } = useAuth();
  const [advisorId, setAdvisorId] = useState(null);
  const advisorName = user?.name || "Advisor";
  const [prices, setPrices] = useState(() => {
    try {
      const cached = localStorage.getItem("advisorPrices");
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  });
  const [priceLoading, setPriceLoading] = useState(false);
  const [lastPriceUpdate, setLastPriceUpdate] = useState(() => {
    try {
      const cached = localStorage.getItem("advisorLastPriceUpdate");
      return cached ? new Date(cached) : null;
    } catch {
      return null;
    }
  });
  const [refreshFlag, setRefreshFlag] = useState(0);

  useEffect(() => {
    const fetchAdvisorId = async () => {
      if (!user?.id) return;
      try {
        const res = await api.get(`/advisor-portal/advisor-users`, {
          params: { custom_user_id: user.id },
        });
        if (Array.isArray(res.data) && res.data.length > 0) {
          setAdvisorId(res.data[0].user_id);
        } else if (res.data && res.data.user_id) {
          setAdvisorId(res.data.user_id);
        }
      } catch (err) {
        setAdvisorId(null);
      }
    };
    fetchAdvisorId();
  }, [user?.id]);

  const { holdings, loading, error, securityTypes } = useAggregatedHoldings(
    "advisor",
    advisorId,
  );
  const performanceData = historicalBalanceData["1Y"];

  // --- Firm View Chart Aggregation Logic (copied exactly) ---
  const allocationMap = {};
  const sectorMap = {};
  const aggregated = {};
  const missing = new Set();
  let missingSectorCount = 0;
  holdings.forEach((h) => {
    const typeName =
      h.asset_class ||
      securityTypes.find((st) => st.security_type_id === h.security_type_id)
        ?.security_type_name ||
      h.security_type_id ||
      "Unknown";
    const normalizedSymbol = getYahooSymbol(h.symbol);
    const price =
      prices[normalizedSymbol] != null ? prices[normalizedSymbol] : null;
    let marketValue = null;
    if (price != null && !isNaN(price)) {
      marketValue = (parseFloat(h.quantity) || 0) * price;
    } else if (h.market_value != null && !isNaN(h.market_value)) {
      marketValue = parseFloat(h.market_value);
      missing.add(h.symbol);
    } else {
      marketValue = 0;
      missing.add(h.symbol);
    }
    allocationMap[typeName] = (allocationMap[typeName] || 0) + marketValue;
    if (
      h.sector &&
      h.sector !== "undefined" &&
      h.sector !== "" &&
      h.sector !== undefined
    ) {
      sectorMap[h.sector] = (sectorMap[h.sector] || 0) + marketValue;
    } else {
      missingSectorCount++;
    }
    const key = `${h.symbol}__${h.security_type_id}`;
    if (!aggregated[key]) {
      aggregated[key] = {
        symbol: h.symbol,
        security_type_id: h.security_type_id,
        asset_class: h.asset_class || "",
        sector: h.sector || "",
        quantity: 0,
        market_value: 0,
        as_of_date: h.as_of_date,
        accounts: new Set(),
      };
    }
    aggregated[key].quantity += parseFloat(h.quantity) || 0;
    aggregated[key].market_value += marketValue;
    aggregated[key].accounts.add(h.account_number);
    if (h.as_of_date > aggregated[key].as_of_date) {
      aggregated[key].as_of_date = h.as_of_date;
    }
  });
  let filteredRows = Object.values(aggregated);
  const allocationChartData = buildAllocationChartData(allocationMap);
  const totalSectors = Object.values(sectorMap).reduce((a, b) => a + b, 0);
  const sectorChartData = buildSectorChartData(sectorMap, totalSectors);
  const allocationChartOptions = sharedPieOptions;
  const sectorChartOptions = sharedBarOptions;

  return (
    <div>
      <h2 className="text-lg font-semibold mb-4">
        Advisor Holdings: {advisorName}
      </h2>
      <div className="grid grid-cols-12 gap-6 mb-6">
        <div className="col-span-4 bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
          <h3 className="text-md font-semibold mb-2">Asset Allocation</h3>
          <div className="h-[300px]">
            <Pie data={allocationChartData} options={allocationChartOptions} />
          </div>
        </div>
        <div className="col-span-4 bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
          <h3 className="text-md font-semibold mb-2">Sector Allocation</h3>
          <div className="h-[300px]">
            <Bar data={sectorChartData} options={sectorChartOptions} />
          </div>
        </div>
        <div className="col-span-4 bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
          <PortfolioPerformanceChart holdings={holdings} />
        </div>
      </div>
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
        <h3 className="text-md font-semibold mb-4">
          Advisor Holdings (Aggregated)
        </h3>
        {lastPriceUpdate && (
          <div className="mb-2 text-gray-500 dark:text-gray-500 text-sm">
            Prices as of {lastPriceUpdate.toLocaleTimeString()}
          </div>
        )}
        {missing.size > 0 && (
          <div className="mb-2 text-yellow-700 bg-yellow-100 rounded p-2 text-sm">
            Warning: No price data for{" "}
            {Array.from(missing).slice(0, 10).join(", ")}
            {missing.size > 10 ? ", ..." : ""}
          </div>
        )}
        {loading && filteredRows.length === 0 ? (
          <TableSkeleton rows={10} />
        ) : error ? (
          <div className="text-red-500">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="text-left text-sm text-gray-600 dark:text-gray-400">
                  <th className="py-3 px-4">Symbol</th>
                  <th className="py-3 px-4">Asset Class</th>
                  <th className="py-3 px-4">Sector</th>
                  <th className="py-3 px-4 text-right">Total Shares</th>
                  <th className="py-3 px-4 text-right">Total Market Value</th>
                  <th className="py-3 px-4 text-right"># Accounts</th>
                  <th className="py-3 px-4 text-right">Latest As of Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-gray-500 dark:text-gray-500 py-4">
                      No holdings found for this advisor.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row, idx) => (
                    <tr
                      key={row.symbol + "-" + row.security_type_id + "-" + idx}
                      className="border-t border-gray-200 dark:border-gray-700"
                    >
                      <td className="py-3 px-4 font-medium text-blue-600">
                        {row.symbol}
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-gray-100">
                        {row.asset_class || ""}
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-gray-100">
                        {row.sector || ""}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                        {row.quantity.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                        {row.market_value
                          ? `$${row.market_value.toLocaleString()}`
                          : "-"}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                        {row.accounts.size}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                        {row.as_of_date}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <SymbolDetailModal
        symbol={selectedSymbol}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        performanceData={performanceData}
      />
    </div>
  );
};

const AccountView = () => {
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  // For demo, use the first account
  const account = mockAccounts[0];
  const { holdings, loading, error } = useAggregatedHoldings(
    "account",
    account.number,
  );
  const performanceData = historicalBalanceData["1Y"];

  return (
    <div>
      <h2 className="text-lg font-semibold mb-4">
        Account Holdings: {account.number}
      </h2>
      <div className="grid grid-cols-12 gap-6 mb-6">
        <div className="col-span-4 bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
          <h3 className="text-md font-semibold mb-2">Asset Allocation</h3>
          <Pie data={assetAllocationData} options={{ responsive: true }} />
        </div>
        <div className="col-span-4 bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
          <h3 className="text-md font-semibold mb-2">Sector Allocation</h3>
          <div className="h-[300px]">
            <Bar data={sectorAllocationData} options={{ responsive: true }} />
          </div>
        </div>
        <div className="col-span-4 bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
          <PortfolioPerformanceChart holdings={holdings} />
        </div>
      </div>
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
        <h3 className="text-md font-semibold mb-4">Account Holdings</h3>
        {loading ? (
          <TableSkeleton rows={10} />
        ) : error ? (
          <div className="text-red-500">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="text-left text-sm text-gray-600 dark:text-gray-400">
                  <th className="py-3 px-4">Symbol</th>
                  <th className="py-3 px-4">Name</th>
                  <th className="py-3 px-4">Shares</th>
                  <th className="py-3 px-4">Market Value</th>
                  <th className="py-3 px-4">Weight</th>
                </tr>
              </thead>
              <tbody>
                {holdings.map((row, idx) => (
                  <tr
                    key={row.symbol + "-" + idx}
                    className="border-t border-gray-200 dark:border-gray-700"
                  >
                    <td
                      className="py-3 px-4 font-medium text-blue-600 cursor-pointer"
                      onClick={() => {
                        setSelectedSymbol(row.symbol);
                        setModalOpen(true);
                      }}
                    >
                      {row.symbol}
                    </td>
                    <td className="py-3 px-4">{row.name}</td>
                    <td className="py-3 px-4">{row.shares}</td>
                    <td className="py-3 px-4">
                      ${row.marketValue?.toLocaleString()}
                    </td>
                    <td className="py-3 px-4">{row.weight}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <SymbolDetailModal
        symbol={selectedSymbol}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        performanceData={performanceData}
      />
    </div>
  );
};

// 1. Place historicalBalanceData, assetAllocationData, sectorAllocationData at the top (after mock data)
const historicalBalanceData = {
  "1M": {
    labels: Array.from({ length: 30 }, (_, i) => `Day ${i + 1}`),
    datasets: [
      {
        label: "Balance",
        data: Array.from(
          { length: 30 },
          () => Math.random() * 1000000 + 1000000,
        ),
        borderColor: "#3B82F6",
        tension: 0.4,
      },
    ],
  },
  "3M": {
    labels: Array.from({ length: 12 }, (_, i) => `Week ${i + 1}`),
    datasets: [
      {
        label: "Balance",
        data: Array.from(
          { length: 12 },
          () => Math.random() * 1000000 + 1000000,
        ),
        borderColor: "#3B82F6",
        tension: 0.4,
      },
    ],
  },
  "6M": {
    labels: Array.from({ length: 24 }, (_, i) => `Week ${i + 1}`),
    datasets: [
      {
        label: "Balance",
        data: Array.from(
          { length: 24 },
          () => Math.random() * 1000000 + 1000000,
        ),
        borderColor: "#3B82F6",
        tension: 0.4,
      },
    ],
  },
  "1Y": {
    labels: Array.from({ length: 12 }, (_, i) => `Month ${i + 1}`),
    datasets: [
      {
        label: "Balance",
        data: Array.from(
          { length: 12 },
          () => Math.random() * 1000000 + 1000000,
        ),
        borderColor: "#3B82F6",
        tension: 0.4,
      },
    ],
  },
  "3Y": {
    labels: Array.from({ length: 36 }, (_, i) => `Month ${i + 1}`),
    datasets: [
      {
        label: "Balance",
        data: Array.from(
          { length: 36 },
          () => Math.random() * 1000000 + 1000000,
        ),
        borderColor: "#3B82F6",
        tension: 0.4,
      },
    ],
  },
  "5Y": {
    labels: Array.from({ length: 60 }, (_, i) => `Month ${i + 1}`),
    datasets: [
      {
        label: "Balance",
        data: Array.from(
          { length: 60 },
          () => Math.random() * 1000000 + 1000000,
        ),
        borderColor: "#3B82F6",
        tension: 0.4,
      },
    ],
  },
};

const assetAllocationData = {
  labels: [
    "U.S. Large Cap",
    "U.S. Mid Cap",
    "U.S. Small Cap",
    "U.S. Real Estate",
    "Int'l Stocks",
    "U.S. Bonds",
  ],
  datasets: [
    {
      data: [55.14, 11.09, 3.21, 0.74, 1.32, 12.2],
      backgroundColor: [
        "#8B0000",
        "#CD853F",
        "#4682B4",
        "#800080",
        "#20B2AA",
        "#90EE90",
      ],
    },
  ],
};

const sectorAllocationData = {
  labels: [
    "Basic Materials",
    "Consumer Cyclical",
    "Financial Services",
    "Real Estate",
    "Consumer Defensive",
    "Healthcare",
    "Utilities",
    "Communication Services",
    "Energy",
    "Industrials",
    "Technology",
    "Other Equity",
    "Non-Equity Assets",
  ],
  datasets: [
    {
      data: [3, 8, 12, 4, 6, 10, 2, 8, 5, 7, 25, 3, 7],
      backgroundColor: "#3B82F6",
      barThickness: 20,
    },
  ],
};

// 2. Move useAggregatedHoldings above all views
// These need to be defined as functions in Allocations, but ensure they are not referenced before definition.

const useAggregatedHoldings = (level, id) => {
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [securityTypes, setSecurityTypes] = useState([]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    const fetchSecurityTypes = async () => {
      try {
        const res = await getSecurityTypes();
        if (isMounted) setSecurityTypes(res.data || []);
      } catch (err) {
        if (isMounted) setSecurityTypes([]);
      }
    };

    const fetchHoldings = async () => {
      try {
        let params = {};
        if (level === "account" && id) {
          params.account_number = id;
        } else if (level === "household" && id) {
          params.household_id = id;
        } else if (level === "advisor" && id) {
          params.advisor_id = id;
        }
        const res = await getHoldings(params);
        if (isMounted) setHoldings(res.data || []);
      } catch (err) {
        if (isMounted) setError(err.message || "Failed to fetch holdings");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSecurityTypes();
    fetchHoldings();
    return () => {
      isMounted = false;
    };
  }, [level, id]);

  return { holdings, loading, error, securityTypes };
};

// --- Shared Chart Utilities ---
const ASSET_CLASS_ORDER = Object.freeze([
  "U.S. Large Cap",
  "International Stocks",
  "U.S. Mid Cap",
  "U.S. Small Cap",
  "U.S. Real Estate",
  "U.S. Bonds",
  "Cash & Cash Equivalents",
  "Digital Assets",
]);
const SECTOR_ORDER = Object.freeze([
  "Technology",
  "Consumer Cyclical",
  "Healthcare",
  "Financial Services",
  "Real Estate",
  "Other Equity",
  "Industrials",
  "Non-Equity Assets",
  "Energy",
  "Consumer Defensive",
  "Utilities",
  "Communication Services",
  "Basic Materials",
  "undefined",
]);
const PIE_COLORS = [
  "#8B0000",
  "#4682B4",
  "#CD853F",
  "#6495ED",
  "#800080",
  "#90EE90",
  "#FFD700",
  "#FF69B4",
  "#A0522D",
  "#2E8B57",
  "#FF7F50",
  "#20B2AA",
  "#DC143C",
  "#008B8B",
  "#B8860B",
  "#006400",
  "#B22222",
  "#FF8C00",
  "#9932CC",
  "#8FBC8F",
];
const BAR_COLORS = [
  "#3B82F6",
  "#6366F1",
  "#10B981",
  "#F59E42",
  "#EF4444",
  "#A21CAF",
  "#FBBF24",
  "#F472B6",
  "#6B7280",
  "#F87171",
  "#2563EB",
  "#D97706",
  "#047857",
  "#AAAAAA",
];
function getColorByIndex(index, palette) {
  return palette[index % palette.length];
}
function buildAllocationChartData(allocationMap) {
  // Always use a fresh array for labels
  const labels = Array.from(ASSET_CLASS_ORDER);
  const data = labels.map((l) => allocationMap[l] || 0);
  const backgroundColor = labels.map((_, i) => getColorByIndex(i, PIE_COLORS));
  return {
    labels,
    datasets: [{ data, backgroundColor }],
  };
}
function buildSectorChartData(sectorMap, totalSectors) {
  // Always use a fresh array for labels
  const labels = Array.from(SECTOR_ORDER);
  const data = labels.map((l) =>
    totalSectors ? ((sectorMap[l] || 0) / totalSectors) * 100 : 0,
  );
  const backgroundColor = labels.map((_, i) => getColorByIndex(i, BAR_COLORS));
  return {
    labels,
    datasets: [{ data, backgroundColor, barThickness: 20 }],
  };
}
const sharedPieOptions = {
  maintainAspectRatio: false,
  plugins: {
    legend: { display: true, position: "top" },
    tooltip: {
      callbacks: {
        label: function (context) {
          const label = context.label || "";
          const value = context.raw || 0;
          return `${label}: $${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
        },
      },
    },
  },
};
const sharedBarOptions = {
  maintainAspectRatio: false,
  indexAxis: "y",
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: function (context) {
          const label = context.label || "";
          const value = context.raw || 0;
          return `${label}: ${value.toFixed(1)}%`;
        },
      },
    },
  },
  scales: {
    x: {
      min: 0,
      max: 100,
      ticks: {
        stepSize: 10,
        callback: function (value) {
          return value.toFixed(0) + "%";
        },
      },
    },
  },
};

const Allocations = () => {
  const { demoStep, showModal, onAction, pauseDemo, resumeDemo } = useDemo();
  const [seen, setSeen] = useState({ ovr: false, perf: false, hold: false, ret: false });
  const { householdId } = useParams();
  const { theme, isDarkMode } = useTheme();
  const [activeView, setActiveView] = useState("firm");
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const [selectedHousehold, setSelectedHousehold] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [timeFrame, setTimeFrame] = useState("1Y");
  const [searchQuery, setSearchQuery] = useState("");
  const [realHoldings, setRealHoldings] = useState([]);
  const [holdingsLoading, setHoldingsLoading] = useState(false);
  const [holdingsError, setHoldingsError] = useState(null);
  const [householdHoldings, setHouseholdHoldings] = useState([]);
  const [householdHoldingsLoading, setHouseholdHoldingsLoading] =
    useState(false);
  const [householdHoldingsError, setHouseholdHoldingsError] = useState(null);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [selectedAllocation, setSelectedAllocation] = useState(null);

  // Define getBreadcrumbItems
  const getBreadcrumbItems = () => {
    const items = [
      { label: "Portfolio Management", link: "/portfolio-management" },
      {
        label: "Firm Allocations",
        link: "/portfolio-management/firm-allocations",
      },
    ];
    if (selectedAdvisor) {
      items.push({ label: selectedAdvisor.name, link: "#" });
    }
    if (selectedHousehold) {
      items.push({ label: selectedHousehold.name, link: "#" });
    }
    if (selectedAccount) {
      items.push({ label: `Account ${selectedAccount.number}`, link: "#" });
    }
    return items;
  };

  // Define handleGlobalSearch
  const handleGlobalSearch = (result) => {
    if (result.type === "advisor") {
      setActiveView("advisor");
      setSelectedAdvisor(result.data);
      setSelectedHousehold(null);
      setSelectedAccount(null);
    } else if (result.type === "household") {
      setActiveView("household");
      setSelectedHousehold(result.data);
      setSelectedAccount(null);
    } else if (result.type === "account") {
      setActiveView("account");
      setSelectedAccount(result.data);
    }
  };

  // Time frame toggle component
  const TimeFrameToggle = () => (
    <div className="flex space-x-2 mb-4">
      {["1M", "3M", "6M", "1Y", "3Y", "5Y"].map((frame) => (
        <button
          key={frame}
          className={`px-3 py-1 rounded ${
            timeFrame === frame
              ? "bg-blue-600 text-white"
              : "bg-gray-200 text-gray-700 dark:text-gray-300 hover:bg-gray-300"
          }`}
          onClick={() => setTimeFrame(frame)}
        >
          {frame}
        </button>
      ))}
    </div>
  );

  // View toggle component
  const ViewToggle = () => (
    <div className="mb-6">
      <div className="flex flex-col space-y-4">
        {/* View Navigation */}
        <div className="flex space-x-4">
          <button
            className={`px-4 py-2 rounded-lg ${activeView === "firm" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
            onClick={() => {
              setActiveView("firm");
              setSelectedAdvisor(null);
              setSelectedHousehold(null);
              setSelectedAccount(null);
            }}
          >
            Firm View
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${activeView === "advisor" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
            onClick={() => {
              setActiveView("advisor");
              setSelectedAdvisor(mockAdvisors[0]);
              setSelectedHousehold(null);
              setSelectedAccount(null);
            }}
          >
            Advisor View
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${activeView === "household" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
            onClick={() => {
              setActiveView("household");
              setSelectedAccount(null);
            }}
          >
            Household View
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${activeView === "account" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
            onClick={() => setActiveView("account")}
          >
            Account View
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${activeView === "aum-upload" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
            onClick={() => setActiveView("aum-upload")}
          >
            AUM Upload
          </button>
        </div>

        {/* Context Display */}
        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
          {selectedAdvisor && (
            <>
              <span>Advisor:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {selectedAdvisor.name}
              </span>
              {(selectedHousehold || selectedAccount) && (
                <span className="mx-2">→</span>
              )}
            </>
          )}
          {selectedHousehold && (
            <>
              <span>Household:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {selectedHousehold.name}
              </span>
              {selectedAccount && <span className="mx-2">→</span>}
            </>
          )}
          {selectedAccount && (
            <>
              <span>Account:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {selectedAccount.number}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const FirmView = () => {
    // Move useState declarations to the very top
    const [assetClassFilter, setAssetClassFilter] = useState(null);
    const [sectorFilter, setSectorFilter] = useState(null);
    const [sortConfig, setSortConfig] = useState({
      key: null,
      direction: "asc",
    });
    const { holdings, loading, error, securityTypes } =
      useAggregatedHoldings("firm");
    const [prices, setPrices] = useState(() => {
      // Try to load cached prices from localStorage
      try {
        const cached = localStorage.getItem("firmPrices");
        return cached ? JSON.parse(cached) : {};
      } catch {
        return {};
      }
    });
    const [cachedHoldings, setCachedHoldings] = useState(() => {
      try {
        const cached = localStorage.getItem("firmHoldings");
        return cached ? JSON.parse(cached) : [];
      } catch {
        return [];
      }
    });
    const [priceLoading, setPriceLoading] = useState(false);
    const [lastPriceUpdate, setLastPriceUpdate] = useState(() => {
      try {
        const cached = localStorage.getItem("firmLastPriceUpdate");
        return cached ? new Date(cached) : null;
      } catch {
        return null;
      }
    });
    const [refreshFlag, setRefreshFlag] = useState(0);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

    // On first mount, if cached holdings exist, use them until real holdings load
    const displayHoldings =
      !loading && holdings.length > 0
        ? holdings
        : loading && cachedHoldings.length > 0
          ? cachedHoldings
          : holdings;

    // Fetch prices for all unique symbols in holdings, batching requests
    useEffect(() => {
      let cancelled = false;
      let uniqueSymbols = Array.from(
        new Set(holdings.map((h) => h.symbol).filter(Boolean)),
      );
      if (uniqueSymbols.length === 0) return;
      setPriceLoading(true);
      fetchPrices(uniqueSymbols).then((data) => {
        if (!cancelled) {
          setPrices(data);
          setPriceLoading(false);
          setLastPriceUpdate(new Date());
          setHasLoadedOnce(true);
          // Cache prices and last update
          try {
            localStorage.setItem("firmPrices", JSON.stringify(data));
            localStorage.setItem(
              "firmLastPriceUpdate",
              new Date().toISOString(),
            );
          } catch {}
        }
      });
      return () => {
        cancelled = true;
      };
    }, [holdings, refreshFlag]);

    // Cache holdings after successful load
    useEffect(() => {
      if (!loading && holdings.length > 0) {
        try {
          localStorage.setItem("firmHoldings", JSON.stringify(holdings));
        } catch {}
      }
    }, [holdings, loading]);

    // Auto-refresh prices every 5 minutes
    useEffect(() => {
      if (!holdings.length) return;
      const interval = setInterval(
        () => {
          setRefreshFlag((f) => f + 1);
        },
        5 * 60 * 1000,
      ); // 5 minutes
      return () => clearInterval(interval);
    }, [holdings]);

    // Use displayHoldings instead of holdings for aggregation
    const allocationMap = {};
    const sectorMap = {};
    const aggregated = {};
    const missing = new Set();
    let missingSectorCount = 0;
    displayHoldings.forEach((h) => {
      const typeName =
        h.asset_class ||
        securityTypes.find((st) => st.security_type_id === h.security_type_id)
          ?.security_type_name ||
        h.security_type_id ||
        "Unknown";
      // Use standardized symbol normalization for price lookup
      const normalizedSymbol = getYahooSymbol(h.symbol);
      const price =
        prices[normalizedSymbol] != null ? prices[normalizedSymbol] : null;
      let marketValue = null;
      if (price != null && !isNaN(price)) {
        marketValue = (parseFloat(h.quantity) || 0) * price;
      } else if (h.market_value != null && !isNaN(h.market_value)) {
        marketValue = parseFloat(h.market_value);
        missing.add(h.symbol);
      } else {
        marketValue = 0;
        missing.add(h.symbol);
      }
      allocationMap[typeName] = (allocationMap[typeName] || 0) + marketValue;
      // Only add to sectorMap if sector is valid
      if (
        h.sector &&
        h.sector !== "undefined" &&
        h.sector !== "" &&
        h.sector !== undefined
      ) {
        sectorMap[h.sector] = (sectorMap[h.sector] || 0) + marketValue;
      } else {
        missingSectorCount++;
      }
      const key = `${h.symbol}__${h.security_type_id}`;
      if (!aggregated[key]) {
        aggregated[key] = {
          symbol: h.symbol,
          security_type_id: h.security_type_id,
          asset_class: h.asset_class || "",
          sector: h.sector || "",
          quantity: 0,
          market_value: 0,
          as_of_date: h.as_of_date,
          accounts: new Set(),
        };
      }
      aggregated[key].quantity += parseFloat(h.quantity) || 0;
      aggregated[key].market_value += marketValue;
      aggregated[key].accounts.add(h.account_number);
      if (h.as_of_date > aggregated[key].as_of_date) {
        aggregated[key].as_of_date = h.as_of_date;
      }
    });
    if (missingSectorCount > 0) {
      logger.warn(
        `There are ${missingSectorCount} holdings with missing or undefined sector. These are excluded from the chart.`,
      );
    }
    // Build sectorLabels and sectorColors only from valid sector names
    const sectorLabels = Object.keys(sectorMap);
    const allocationLabels = Object.keys(allocationMap);
    const sectorColors = getFilteredColors(
      sectorLabels,
      sectorFilter,
      getColorPalette(sectorLabels.length, "bar"),
    );

    // Compute total for percent calculations
    const totalAssets = Object.values(allocationMap).reduce((a, b) => a + b, 0);
    const totalSectors = Object.values(sectorMap).reduce((a, b) => a + b, 0);

    // Chart color logic for filtering
    function getFilteredColors(labels, selectedLabel, palette) {
      return labels.map(
        (label) =>
          !selectedLabel || label === selectedLabel
            ? palette[labels.indexOf(label) % palette.length]
            : "#d1d5db", // Tailwind gray-300
      );
    }
    const allocationColors = getFilteredColors(
      allocationLabels,
      assetClassFilter,
      getColorPalette(allocationLabels.length, "pie"),
    );

    // Find the max sector percent for axis scaling
    const maxSectorValue = Math.max(...Object.values(sectorMap), 0);
    const maxSectorPercent = totalSectors
      ? (maxSectorValue / totalSectors) * 100
      : 0;
    // Find the next highest 5% increment above max, then add one more 5%
    const axisMaxPercent = Math.ceil(maxSectorPercent / 5) * 5 + 5;

    const allocationChartOptions = {
      maintainAspectRatio: false,
      onHover: (event, elements) => {
        if (event.native) {
          event.native.target.style.cursor =
            elements && elements.length ? "pointer" : "default";
        }
      },
      onClick: (evt, elems) => handlePieClick(elems),
      plugins: {
        tooltip: {
          callbacks: {
            label: function (context) {
              const label = context.label || "";
              const value = allocationMap[label] || 0;
              return `${label}: $${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
            },
          },
        },
      },
    };
    const sectorChartOptions = {
      maintainAspectRatio: false,
      indexAxis: "y",
      onHover: (event, elements) => {
        if (event.native) {
          event.native.target.style.cursor =
            elements && elements.length ? "pointer" : "default";
        }
      },
      onClick: (evt, elems) => handleBarClick(elems),
      scales: {
        x: {
          min: 0,
          max: axisMaxPercent,
          ticks: {
            stepSize: 5,
            callback: function (value) {
              return value.toFixed(0) + "%";
            },
          },
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: function (context) {
              // context.label is the sector name
              // context.parsed.x is the percent value
              const label = context.label || "";
              const sector = label;
              const value = sectorMap[sector] || 0;
              return `${label}: $${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
            },
          },
        },
      },
    };

    // Chart data with filtered colors
    const allocationChartData = {
      labels: allocationLabels,
      datasets: [
        {
          data: allocationLabels.map((l) => allocationMap[l]),
          backgroundColor: allocationColors,
        },
      ],
    };

    // Final defensive filter before building sector chart data
    const filteredSectorLabels = sectorLabels.filter(
      (l) =>
        l && l !== "undefined" && l !== undefined && l !== "null" && l !== null,
    );
    const filteredSectorColors = sectorColors.slice(
      0,
      filteredSectorLabels.length,
    );
    const filteredSectorData = filteredSectorLabels.map((l) =>
      totalSectors ? (sectorMap[l] / totalSectors) * 100 : 0,
    );

    const sectorChartData = {
      labels: filteredSectorLabels,
      datasets: [
        {
          data: filteredSectorData,
          backgroundColor: filteredSectorColors,
          barThickness: 20,
        },
      ],
    };

    // Chart click handlers
    const handlePieClick = (elems) => {
      if (elems && elems.length > 0) {
        const idx = elems[0].index;
        const label = allocationLabels[idx];
        setAssetClassFilter(label);
      }
    };
    const handleBarClick = (elems) => {
      if (elems && elems.length > 0) {
        const idx = elems[0].index;
        const label = sectorLabels[idx];
        setSectorFilter(label);
      }
    };
    const handleClearFilters = () => {
      setAssetClassFilter(null);
      setSectorFilter(null);
    };

    // Filtered and sorted rows
    let filteredRows = Object.values(aggregated);
    if (assetClassFilter)
      filteredRows = filteredRows.filter(
        (row) => row.asset_class === assetClassFilter,
      );
    if (sectorFilter)
      filteredRows = filteredRows.filter((row) => row.sector === sectorFilter);
    if (sortConfig.key) {
      filteredRows = [...filteredRows].sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return (
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Firm Holdings
          </h2>
          {loading ? (
            <TableSkeleton rows={10} />
          ) : error ? (
            <div className="text-red-500">{error}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-600 dark:text-gray-400">
                    <th className="py-3 px-4">Symbol</th>
                    <th className="py-3 px-4">Asset Class</th>
                    <th className="py-3 px-4">Sector</th>
                    <th className="py-3 px-4 text-right">Total Shares</th>
                    <th className="py-3 px-4 text-right">Total Market Value</th>
                    <th className="py-3 px-4 text-right"># Accounts</th>
                    <th className="py-3 px-4 text-right">Latest As of Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row, idx) => (
                    <tr
                      key={row.symbol + "-" + row.security_type_id + "-" + idx}
                      className="border-t border-gray-200 dark:border-gray-700"
                    >
                      <td className="py-3 px-4 font-medium text-blue-600">
                        {row.symbol}
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-gray-100">
                        {row.asset_class || ""}
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-gray-100">
                        {row.sector || ""}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                        {row.quantity.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                        {row.market_value
                          ? `$${row.market_value.toLocaleString()}`
                          : "-"}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                        {row.accounts.size}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                        {row.as_of_date}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen w-full ${isDarkMode ? 'dark' : ''}`}>
      <SubPageLayout
        title="Portfolio Allocations"
        subtitle="View and analyze portfolio allocations across different asset classes"
        breadcrumbItems={getBreadcrumbItems()}
      >
        <GlobalSearch onSearch={handleGlobalSearch} />
        <ViewToggle />
        {activeView === "firm" && <FirmView />}
        {activeView === "advisor" && <AdvisorView />}
        {activeView === "household" && <HouseholdView />}
        {activeView === "account" && <AccountView />}
        {activeView === "aum-upload" && <CSVUpload />}
      </SubPageLayout>
    </div>
  );
};

// Utility: Normalize symbol for Yahoo
const normalizeSymbol = (symbol) => {
  if (!symbol) return symbol;
  return symbol.trim().toUpperCase().replace(/\./g, "-");
};
const symbolMap = {
  BRKB: "BRK-B",
};
function getYahooSymbol(symbol) {
  return symbolMap[symbol] || normalizeSymbol(symbol);
}
// Utility: Fetch prices from yfinance Flask service
const fetchPrices = async (symbols, batchSize = 50) => {
  if (!symbols || symbols.length === 0) return {};
  let allPrices = {};
  for (let i = 0; i < symbols.length; i += batchSize) {
    const batch = symbols.slice(i, i + batchSize).map(getYahooSymbol);
    try {
      const { data } = await api.get(`/market/yahoo/batch`, {
        params: { symbols: batch.join(",") }
      });
      allPrices = { ...allPrices, ...data };
    } catch (err) {
      console.error('Error fetching prices:', err);
    }
  }
  return allPrices;
};
// Utility: Generate color palette
function getColorPalette(count, paletteType = "pie") {
  const pieColors = [
    "#8B0000",
    "#CD853F",
    "#4682B4",
    "#800080",
    "#20B2AA",
    "#90EE90",
    "#FFD700",
    "#FF69B4",
    "#A0522D",
    "#2E8B57",
    "#FF7F50",
    "#6495ED",
    "#DC143C",
    "#008B8B",
    "#B8860B",
    "#006400",
    "#B22222",
    "#FF8C00",
    "#9932CC",
    "#8FBC8F",
  ];
  if (count <= pieColors.length) return pieColors.slice(0, count);
  return Array.from(
    { length: count },
    (_, i) => `hsl(${(i * 360) / count}, 60%, 60%)`,
  );
}

export default Allocations;
